import React, { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { HandshakeIcon } from "../HandshakeIcon";

const TIER_IMG = {
  hs_tier_1: "/handshakeshop/handshakeshop_bronze.webp",
  hs_tier_2: "/handshakeshop/handshakeshop_silver.webp",
  hs_tier_3: "/handshakeshop/handshakeshop_gold.webp",
};
const TIER_ACCENT = { hs_tier_1: "#E39B5B", hs_tier_2: "#DDE6FF", hs_tier_3: "#FFD700" };

export function PremiumCard({ pkg, onBuy, busy }) {
  const accent = TIER_ACCENT[pkg.id] || "#00F0FF";
  return (
    <div data-testid={`premium-card-${pkg.id}`}
         className="wr-rise group relative rounded-2xl p-[2px] transition-transform duration-300 hover:-translate-y-2"
         style={{ background: `linear-gradient(160deg, #00F0FF, ${accent}88 40%, #0A051A 70%, #00F0FF)` }}>
      <div className="relative h-full rounded-[14px] overflow-hidden bg-[#0A051A] flex flex-col items-center text-center px-5 pt-6 pb-5 ps-card-inner">
        {pkg.badge && (
          <div className="absolute top-3 right-3 px-2.5 py-0.5 rounded-full font-heading text-[10px] uppercase tracking-widest z-10"
               style={{ background: "#00F0FF", color: "#0A051A", boxShadow: "0 0 12px #00F0FF" }}>{pkg.badge}</div>
        )}
        <div className="absolute inset-x-0 top-0 h-2/3 pointer-events-none ps-aura" />
        <img src={TIER_IMG[pkg.id]} alt={pkg.name} data-testid={`premium-img-${pkg.id}`}
             className="relative w-48 h-48 sm:w-56 sm:h-56 object-contain ps-glow transition-transform duration-500 group-hover:scale-110" />
        <div className="relative font-graffiti text-2xl mt-2 leading-none" style={{ color: accent, textShadow: `0 0 12px ${accent}88, 2px 2px 0 #000` }}>{pkg.name}</div>
        <div className="relative flex items-center gap-2 mt-3" data-testid={`premium-amount-${pkg.id}`}>
          <HandshakeIcon className="w-7 h-7" />
          <span className="font-num text-3xl text-white tracking-wider">{pkg.handshakes.toLocaleString()}</span>
        </div>
        <button data-testid={`buy-premium-${pkg.id}`} onClick={() => onBuy(pkg.id)} disabled={busy === pkg.id}
                className="relative btn-neon w-full mt-5 text-sm ps-buy">
          {busy === pkg.id ? <Loader2 className="inline animate-spin" size={14} /> : `$${pkg.amount.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}

function BundleItem({ item, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center text-center gap-2 px-2 min-w-[7.5rem] flex-1">
      <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center ps-float">
        {item.kind === "handshakes"
          ? <HandshakeIcon className="w-16 h-16 sm:w-20 sm:h-20" />
          : <img src={item.image} alt={item.label} className="max-w-full max-h-full object-contain ps-glow" />}
      </div>
      <div className="font-heading text-sm text-white leading-tight">{item.label}</div>
      {item.sub && <div className="font-mono text-[11px] text-[#39FF14] leading-tight">{item.sub}</div>}
    </div>
  );
}

export function BundleBanner({ pkg, onBuy, busy }) {
  return (
    <div data-testid={`bundle-banner-${pkg.id}`}
         className="wr-rise group relative rounded-2xl p-[2px] transition-transform duration-300 hover:-translate-y-1"
         style={{ background: "linear-gradient(110deg, #00F0FF, #9D00FF 45%, #FF007F 75%, #00F0FF)" }}>
      <div className="relative rounded-[14px] overflow-hidden bg-[#0A051A] px-5 py-6 sm:px-8 ps-card-inner">
        <div className="absolute inset-0 pointer-events-none ps-aura" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="lg:w-56 shrink-0">
            {pkg.badge && <div className="inline-block px-2.5 py-0.5 mb-2 rounded-full font-heading text-[10px] uppercase tracking-widest"
                               style={{ background: "#00F0FF", color: "#0A051A" }}>One-time · {pkg.badge}</div>}
            <div className="font-graffiti text-3xl leading-none" style={{ color: "#FF3399", textShadow: "0 0 12px #FF007F88, 2px 2px 0 #000" }}>{pkg.name}</div>
          </div>
          <div className="flex-1 flex flex-wrap sm:flex-nowrap justify-around gap-4">
            {(pkg.contents || []).map((it, i) => <BundleItem key={i} item={it} testId={`bundle-item-${pkg.id}-${it.kind}`} />)}
          </div>
          <button data-testid={`buy-premium-${pkg.id}`} onClick={() => onBuy(pkg.id)} disabled={busy === pkg.id}
                  className="btn-neon text-sm lg:w-40 shrink-0 ps-buy">
            {busy === pkg.id ? <Loader2 className="inline animate-spin" size={14} /> : `$${pkg.amount.toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PurchaseCelebration({ data, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2600); return () => clearTimeout(t); }, [onDone]);
  return (
    <div data-testid="purchase-celebration" className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-none ps-celebrate-bg">
      <div className="absolute w-[34rem] h-[34rem] rounded-full ps-ring" />
      <div className="relative flex flex-col items-center ps-celebrate-pop">
        <HandshakeIcon className="w-40 h-40" />
        <div className="font-num text-6xl text-white mt-3 tracking-wider" style={{ textShadow: "0 0 18px #00F0FF, 0 0 40px #00F0FF" }}>
          +{(data.handshakes || 0).toLocaleString()}
        </div>
        <div className="font-graffiti text-3xl mt-1" style={{ color: "#00F0FF", textShadow: "0 0 14px #00F0FF, 2px 2px 0 #000" }}>{data.name || "Handshakes"}</div>
      </div>
    </div>
  );
}
